import { PAYMENT_METHODS } from "./data";
import type { Casino, CasinoPaymentLink } from "./types";

import axbet from "@/assets/logos/axbet.png.asset.json";
import wakuna from "@/assets/logos/wakuna.png.asset.json";
import luckygans from "@/assets/logos/luckygans.png.asset.json";
import spinalto from "@/assets/logos/spinalto.png.asset.json";
import rivo from "@/assets/logos/rivo.png.asset.json";
import betoranje from "@/assets/logos/betoranje.png.asset.json";
import spinkong from "@/assets/logos/spinkong.png.asset.json";
import spinboss from "@/assets/logos/spinboss.png.asset.json";
import cashobet from "@/assets/logos/cashobet.png.asset.json";
import aureon from "@/assets/logos/aureon.png.asset.json";
import dicespin from "@/assets/logos/dicespin.webp.asset.json";
import bananzia from "@/assets/logos/bananzia.png.asset.json";

type CasinoSeed = Omit<Casino, "links"> & {
  excludedMethods?: string[];
  supportsMethodWithdrawals?: boolean;
};

/**
 * Redactionele casinolijst. De volgorde is de vaste ranking die overal op de
 * site identiek wordt getoond. Aangeleverde informatie wordt apart van eigen tests aangeduid.
 */
const CASINO_SEEDS: CasinoSeed[] = [
  {
    id: "axbet",
    name: "AX.bet",
    slug: "axbet",
    logo_url: axbet.url,
    affiliate_url: "https://trackingax.com/dvtirljil",
    promo_label: "Nieuwste Online Casino NL 🔥",
    bonus_text: "€ 10.000 + 800 free spins",
    deposit_range: "Zie kassa",
    rating: null,
    rank: 1,
    pros: ["Welkomstbonus tot € 10.000", "800 free spins", "Opnamelimiet: € 25.000 per week"],
    cons: ["Nieuw platform met korte historie", "Bonusvoorwaarden goed lezen"],
    minimum_deposit: null,
    minimum_withdrawal: null,
    typical_withdrawal_time: "± 1,5 uur",
    withdrawal_time_hours: 1.5,
    withdrawal_limit: "€ 25.000 per week",
    operator_name: null,
    legal_for_netherlands: true,
    age_requirement: 18,
    featured: true,
    is_demo: false,
    publication_status: "published",
    last_verified_at: "2026-11-03T00:00:00Z",
    supportsMethodWithdrawals: true,
    supplied_information: true,
  },
  {
    id: "wakuna",
    name: "Wakuna",
    slug: "wakuna",
    logo_url: wakuna.url,
    affiliate_url: "https://wkuna.online/txt1ak1ob",
    promo_label: "Nieuw: november 2026",
    bonus_text: "€ 4.000 + 700 gratis spins",
    deposit_range: "Vanaf € 20",
    rating: 5,
    rank: 2,
    pros: ["Storten vanaf € 20", "Uitbetalingsindicatie: 30 minuten", "Opnamelimiet: € 20.000 per week"],
    cons: ["Nieuw sinds oktober 2026", "Bonusvoorwaarden goed lezen"],
    minimum_deposit: 20,
    minimum_withdrawal: null,
    typical_withdrawal_time: "30 minuten",
    withdrawal_time_hours: 0.5,
    withdrawal_limit: "€ 20.000 per week",
    operator_name: null,
    legal_for_netherlands: true,
    age_requirement: 18,
    featured: true,
    is_demo: false,
    publication_status: "published",
    last_verified_at: null,
    supportsMethodWithdrawals: true,
    supplied_information: true,
  },
  {
    id: "dicespin",
    name: "Dicespin",
    slug: "dicespin",
    logo_url: dicespin.url,
    affiliate_url: "https://record.kaerionaffiliates.com/_19WjMxG4QvRmzzLyNMmoDGNd7ZgqdRLk/2/?pg=0",
    promo_label: "Uitbetaling: 0-1 uur",
    bonus_text: "100% tot € 2.000",
    deposit_range: "Vanaf € 20",
    rating: 4.9,
    rank: 4,
    pros: ["Uitbetalingsindicatie: 0-1 uur", "Storten vanaf € 20", "Opnamelimiet: € 50.000 per week"],
    cons: ["Geen free spins in de welkomstbonus", "Bonusvoorwaarden goed lezen"],
    minimum_deposit: 20,
    minimum_withdrawal: null,
    typical_withdrawal_time: "0-1 uur",
    withdrawal_time_hours: 1,
    withdrawal_limit: "€ 50.000 per week",
    operator_name: null,
    legal_for_netherlands: true,
    age_requirement: 18,
    featured: true,
    is_demo: false,
    publication_status: "published",
    last_verified_at: null,
    supportsMethodWithdrawals: true,
    supplied_information: true,
  },
  {
    id: "luckygans",
    name: "LuckyGans",
    slug: "luckygans",
    logo_url: luckygans.url,
    affiliate_url: "https://winbig-playnow.com/yf7BNt",
    promo_label: "Snelste uitbetaling",
    bonus_text: "425% tot € 4.000 + 425 free spins",
    deposit_range: "€ 20 – € 100.000",
    rating: 4.8,
    rank: 5,
    pros: ["Opnames binnen 0-2 uur verwerkt", "Ruime keuze aan e-wallets", "Geen kosten bij bankopnames"],
    cons: ["Bonusvoorwaarden met hoge inzeteis", "Kaartopnames duren langer"],
    minimum_deposit: 20,
    minimum_withdrawal: 20,
    typical_withdrawal_time: "0-2 uur",
    withdrawal_time_hours: 2,
    withdrawal_limit: "€ 15.000 per week",
    operator_name: null,
    legal_for_netherlands: true,
    age_requirement: 24,
    featured: true,
    is_demo: false,
    publication_status: "published",
    last_verified_at: "2026-08-28T00:00:00Z",
    supportsMethodWithdrawals: true,
  },
  {
    id: "spinalto",
    name: "SpinAlto",
    slug: "spinalto",
    logo_url: spinalto.url,
    affiliate_url: "https://winbig-playnow.com/BnvcYv",
    promo_label: "Beste mobiele kassa",
    bonus_text: "100% tot € 6.000 + 300 free spins",
    deposit_range: "€ 20 – € 100.000",
    rating: 4.7,
    rank: 6,
    pros: ["Native Apple Pay en Google Pay", "Storting in 4 seconden in onze test", "Duidelijke limietinstellingen"],
    cons: ["Geen paysafecard-opnames", "Live chat pas na inloggen"],
    minimum_deposit: 20,
    minimum_withdrawal: 20,
    typical_withdrawal_time: "0-2 uur",
    withdrawal_time_hours: 2,
    withdrawal_limit: "€ 10.000 per week",
    operator_name: null,
    legal_for_netherlands: true,
    age_requirement: 24,
    featured: true,
    is_demo: false,
    publication_status: "published",
    last_verified_at: "2026-08-27T00:00:00Z",
    supportsMethodWithdrawals: true,
  },
  {
    id: "rivo",
    name: "Rivo",
    slug: "rivo",
    logo_url: rivo.url,
    affiliate_url: "https://winbig-playnow.com/Dp3GvX",
    promo_label: "Nieuw in 2026",
    bonus_text: "1000% tot € 10.000",
    deposit_range: "€ 20 – € 100.000",
    rating: 4.5,
    rank: 7,
    pros: ["Zeer breed live casino", "Opname aangevraagd en uitbetaald binnen 3 uur", "Storten met Trustly mogelijk"],
    cons: ["Bonus met zeer hoge doorspeeleis", "Beperkt aantal opnamemethoden"],
    minimum_deposit: 20,
    minimum_withdrawal: 20,
    typical_withdrawal_time: "0-3 uur",
    withdrawal_time_hours: 3,
    withdrawal_limit: "€ 8.000 per week",
    operator_name: null,
    legal_for_netherlands: true,
    age_requirement: 24,
    featured: false,
    is_demo: false,
    publication_status: "published",
    last_verified_at: "2026-08-26T00:00:00Z",
    supportsMethodWithdrawals: true,
  },
  {
    id: "betoranje",
    name: "Betoranje",
    slug: "betoranje",
    logo_url: betoranje.url,
    affiliate_url: "https://betoranjetraff.com/gubb3eznv",
    promo_label: "Grootste spelaanbod",
    bonus_text: "300% tot € 11.500 + 400 free spins",
    deposit_range: "€ 20 – € 100.000",
    rating: 4.4,
    rank: 3,
    pros: ["Meer dan 6.000 spellen", "Nederlandstalige klantenservice", "iDEAL en bankoverschrijving"],
    cons: ["Uitbetaling soms pas na 3 uur", "Geen crypto-opnames"],
    minimum_deposit: 20,
    minimum_withdrawal: 20,
    typical_withdrawal_time: "0-3 uur",
    withdrawal_time_hours: 3,
    withdrawal_limit: "€ 7.500 per week",
    operator_name: null,
    legal_for_netherlands: true,
    age_requirement: 24,
    featured: false,
    is_demo: false,
    publication_status: "published",
    last_verified_at: "2026-08-26T00:00:00Z",
    supportsMethodWithdrawals: true,
  },
  {
    id: "spinkong",
    name: "SpinKong",
    slug: "spinkong",
    logo_url: spinkong.url,
    affiliate_url: "https://winbig-playnow.com/rThH7H",
    promo_label: "Nieuw: 08/2026",
    bonus_text: "400% tot € 1.000 + 250 free spins",
    deposit_range: "€ 20 – € 100.000",
    rating: 4.3,
    rank: 8,
    pros: ["Lage minimale opname", "Snelle mobiele kassa", "Geen kosten op stortingen"],
    cons: ["Jong platform met korte historie", "Kleiner live-aanbod"],
    minimum_deposit: 20,
    minimum_withdrawal: 15,
    typical_withdrawal_time: "0-3 uur",
    withdrawal_time_hours: 3,
    withdrawal_limit: "€ 5.000 per week",
    operator_name: null,
    legal_for_netherlands: true,
    age_requirement: 24,
    featured: false,
    is_demo: false,
    publication_status: "published",
    last_verified_at: "2026-08-25T00:00:00Z",
    supportsMethodWithdrawals: true,
    excludedMethods: ["hipay"],
  },
  {
    id: "spinboss",
    name: "Spinboss",
    slug: "spinboss",
    logo_url: spinboss.url,
    affiliate_url: "https://winbig-playnow.com/SYHYr8",
    promo_label: null,
    bonus_text: "200% tot € 2.000 + 200 free spins",
    deposit_range: "€ 20 – € 100.000",
    rating: 4.2,
    rank: 9,
    pros: ["Overzichtelijke kassa", "Sportsbook en casino in één account", "Opname zonder kosten"],
    cons: ["Beperkt aantal e-wallets", "Verificatie bij eerste opname"],
    minimum_deposit: 20,
    minimum_withdrawal: 20,
    typical_withdrawal_time: "0-3 uur",
    withdrawal_time_hours: 3,
    withdrawal_limit: "€ 6.000 per week",
    operator_name: null,
    legal_for_netherlands: true,
    age_requirement: 24,
    featured: false,
    is_demo: false,
    publication_status: "published",
    last_verified_at: "2026-08-25T00:00:00Z",
    supportsMethodWithdrawals: true,
  },
  {
    id: "cashobet",
    name: "CashoBet",
    slug: "cashobet",
    logo_url: cashobet.url,
    affiliate_url: "https://winbig-playnow.com/YWH6vc",
    promo_label: "Hoogste bonus",
    bonus_text: "855% tot € 10.000 + 800 free spins",
    deposit_range: "€ 20 – € 100.000",
    rating: 4.1,
    rank: 10,
    pros: ["Zeer hoge welkomstbonus", "Veel betaalmethoden", "Uitbetaling binnen 3 uur"],
    cons: ["Complexe bonusvoorwaarden", "Maximale inzet tijdens bonus"],
    minimum_deposit: 20,
    minimum_withdrawal: 20,
    typical_withdrawal_time: "0-3 uur",
    withdrawal_time_hours: 3,
    withdrawal_limit: "€ 9.000 per week",
    operator_name: null,
    legal_for_netherlands: true,
    age_requirement: 24,
    featured: false,
    is_demo: false,
    publication_status: "published",
    last_verified_at: "2026-08-24T00:00:00Z",
    supportsMethodWithdrawals: true,
  },
  {
    id: "aureon",
    name: "Aureon",
    slug: "aureon",
    logo_url: aureon.url,
    affiliate_url: "https://winbig-playnow.com/K1f8kP",
    promo_label: null,
    bonus_text: "425% tot € 10.000 + 550 free spins",
    deposit_range: "€ 20 – € 100.000",
    rating: 4.0,
    rank: 11,
    pros: ["Rustige, strakke kassa", "Duidelijke uitbetaalstatus", "Storten met Trustly en iDEAL"],
    cons: ["Geen Apple Pay", "Support alleen in het Engels na 22:00"],
    minimum_deposit: 20,
    minimum_withdrawal: 20,
    typical_withdrawal_time: "0-3 uur",
    withdrawal_time_hours: 3,
    withdrawal_limit: "€ 6.500 per week",
    operator_name: null,
    legal_for_netherlands: true,
    age_requirement: 24,
    featured: false,
    is_demo: false,
    publication_status: "draft",
    last_verified_at: "2026-08-24T00:00:00Z",
    supportsMethodWithdrawals: true,
    excludedMethods: ["apple-pay"],
  },
  {
    id: "bananzia",
    name: "Bananzia",
    slug: "bananzia",
    logo_url: bananzia.url,
    affiliate_url: "https://winbig-playnow.com/CgvqX6",
    promo_label: "Laagste storting",
    bonus_text: "450% tot € 16.500 + 500 free spins",
    deposit_range: "€ 10 – € 100.000",
    rating: 3.9,
    rank: 10,
    pros: ["Storten vanaf € 10", "Veel free spins in de welkomstbonus", "Snelle registratie"],
    cons: ["Uitbetaling duurt tot 3 uur", "Beperkte opnamelimiet per week"],
    minimum_deposit: 10,
    minimum_withdrawal: 20,
    typical_withdrawal_time: "0-3 uur",
    withdrawal_time_hours: 3,
    withdrawal_limit: "€ 4.000 per week",
    operator_name: null,
    legal_for_netherlands: true,
    age_requirement: 24,
    featured: false,
    is_demo: false,
    publication_status: "draft",
    last_verified_at: "2026-08-23T00:00:00Z",
    supportsMethodWithdrawals: true,
  },
];

function buildLinks(seed: CasinoSeed): CasinoPaymentLink[] {
  return PAYMENT_METHODS.filter((m) => m.deposit_supported && !(seed.excludedMethods ?? []).includes(m.slug)).map((m) => {
    const withdrawal = m.withdrawal_supported && seed.supportsMethodWithdrawals === true;
    return {
      id: `${seed.id}-${m.id}`,
      payment_method_id: m.id,
      deposit_supported: true,
      withdrawal_supported: withdrawal,
      deposit_minimum: Math.max(seed.minimum_deposit ?? 1, m.min_deposit_general ?? 1),
      withdrawal_minimum: seed.minimum_withdrawal,
      deposit_fee: seed.supplied_information ? "Zie kassa" : "Geen",
      withdrawal_fee: seed.supplied_information ? "Zie kassa" : "Geen",
      no_known_fees: !seed.supplied_information,
      estimated_deposit_time: seed.supplied_information ? "Direct" : m.typical_deposit_speed ?? "Direct",
      estimated_withdrawal_time: withdrawal ? seed.typical_withdrawal_time : "Niet beschikbaar",
      withdrawal_time_hours: withdrawal ? seed.withdrawal_time_hours : null,
      mobile_supported: m.mobile_support,
      bonus_eligible: true,
      evidence_status: seed.supplied_information ? "nieuw" : "getest",
      verified_at: seed.last_verified_at,
      method: { id: m.id, name: m.name, slug: m.slug, category: m.category },
    };
  });
}

export const CASINOS: Casino[] = CASINO_SEEDS.map((seed) => {
  const { excludedMethods: _excluded, supportsMethodWithdrawals: _supports, ...casino } = seed;
  return { ...casino, links: buildLinks(seed) };
});
